import React, { ChangeEvent, FC, useState } from 'react';
import { Box, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { addActionSetTemplate } from 'apollo/templates/action-sets';
import { LoadingButton } from '@mui/lab';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { ActionSet } from 'models/action-set';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import CurrencyInput from 'components/shared/currency-input/currency-input.component';
import { Currency } from 'models/country';

type AddActionSetDialogProps = {
    onClose: (actionSet?: ActionSet) => void;
    open: boolean;
};

const AddActionSetDialog: FC<AddActionSetDialogProps> = ({ onClose, open }: AddActionSetDialogProps) => {
    const { t: translate } = useTranslation(['common', 'manage-actions']);
    const { showBoundary } = useDenthubErrorBoundary();

    const [title, setTitle] = useState('');
    const [currency, setCurrency] = useState<Currency>({ code: 'SEK' } as Currency);
    const [validationErrors, setValidationErrors] = useState<{ title?: string }>({});

    const [addActionSet, { loading }] = useMutation(addActionSetTemplate, {
        onCompleted({ addActionSet: actionSet }) {
            onClose(actionSet);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const handleSave = () => {
        if (!title) {
            setValidationErrors({ title: translate('manage-actions:TITLE_REQUIRED') });
            return;
        }
        addActionSet({
            variables: { title, currency: currency.code },
        });
    };

    const onTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const title = event.target.value;
        setTitle(title);
    };

    return (
        <DHDialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            title={translate('manage-actions:ADD_ACTION_SET')}
            actions={
                <LoadingButton loading={loading} onClick={() => handleSave()} variant="contained">
                    {translate('common:SAVE')}
                </LoadingButton>
            }
        >
            <TextField
                id="title"
                fullWidth
                required={true}
                label={translate('manage-actions:TITLE')}
                type="text"
                margin="dense"
                helperText={validationErrors?.title}
                error={!!validationErrors?.title}
                onChange={onTitleChange}
                name="title"
            />
            <Box mt={2}>
                <CurrencyInput currency={currency} onCurrencySelect={setCurrency} />
            </Box>
        </DHDialog>
    );
};

export default AddActionSetDialog;
